import { Auth0ContextInterface, useAuth0 } from "@auth0/auth0-react";
import { Button } from "@chakra-ui/react/button";
import { Flex } from "@chakra-ui/react/flex";
import { useBreakpointValue } from "@chakra-ui/react/hooks";
import { Icon } from "@chakra-ui/react/icon";
import { Spinner } from "@chakra-ui/react/spinner";
import { Text } from "@chakra-ui/react/text";
import { useQueryClient } from "@tanstack/react-query";
import { useColorMode } from "components/ui/color-mode";
import { toaster } from "components/ui/toaster";
import { MerchantContext } from "contexts/MerchantContext";
import {
  AppIsOutdatedMessage,
  ChannelCreatedMessage,
  InAppNotificationCreated,
  SocketAgentUpdated,
  SocketTagCreated,
  SocketTagDeleted,
} from "entities/ISocketArgs";
import { IntegrationName } from "entities/domain/admin/merchants/merchant-integrations";
import TagsDomain from "entities/domain/tags/tags-domain";
import {
  currentAgentQueryKey,
  useCurrentAgent,
  useUnreadCounts,
} from "hooks/queries/use-current-agent";
import { removeTagFromCache, tagsQueryKey } from "hooks/queries/use-group";
import {
  appendNotificationToCache,
  notificationsQueryKey,
} from "hooks/queries/use-notifications";
import {
  merchantQueryKey,
  useMerchantQuery,
} from "hooks/queries/use-settings-merchant";
import usePrefetchConversations from "hooks/queries/use-prefetch-conversations";
import { useMerchantAgents } from "hooks/queries/use-teammates";
import { useWebSocket } from "hooks/use-socket";
import React, { ReactNode, Suspense, useEffect, useRef, useState } from "react";
import { LuRefreshCcw } from "react-icons/lu";
import { useLocation } from "react-router-dom";
import { setColorMode, setColorScheme } from "redux/features/theme";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import AgentsService from "services/agents";
import AttributeService from "services/attributes";
import CustomFieldService from "services/customFields";
import { fromDtoToDomain } from "services/notifications";
import TagsService from "services/tags";

import { askForNotificationsPermission } from "util/browser_notifications";
import { endSession } from "util/endSession";
import { lazyWithRetry } from "util/lazyWithRetry";
import { colorList, isMobileApp } from "util/methods";
import { canAccessAiChats } from "util/permissions";
import PrivatePageWrapper from "./PrivatePageWrapper";

// Lazy-loaded so the AI-chat widget tree stays off the initial/eager bundle: it
// mounts hidden (isOpen=false) and its transitive imports (TimelineItem →
// ActionProposalCard → SmartFields → QueryBuilder + campaigns/templates
// services) were leaking a large amount of non-critical code into the entry
// chunk. It loads in the background once the shell has rendered.
const AiChatWidget = lazyWithRetry(
  () => import("components/ai-chats/AiChatWidget")
);
const AiChatLauncher = lazyWithRetry(
  () => import("components/ai-chats/AiChatLauncher")
);

const PrivateRoute = ({ children }: { children?: ReactNode }) => {
  const auth0Context = useAuth0();
  const { user }: Auth0ContextInterface = useAuth0();
  const { colorMode } = useColorMode();
  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );
  const routerLocation = useLocation();
  const isAdminRoute = routerLocation.pathname.split("/").includes("admin");
  const { isInitialLoading } = useAppSelector((state) => state.merchant);
  const { data: merchant } = useMerchantQuery();
  const { data: currentAgent } = useCurrentAgent();
  const { data: agents = [], isLoading: agentsLoading } = useMerchantAgents(
    merchant?.id
  );
  // Kick off the inbox conversations fetch from the eager shell so it doesn't
  // wait for the lazy chat chunk to load and mount the list.
  usePrefetchConversations();
  const { socket, addEventHandler, removeEventHandler } = useWebSocket();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const toastTextRef = useRef<HTMLParagraphElement>(null);

  const [notificationPermissionRequested, setNotificationPermissionRequested] =
    useState<boolean>(false);

  const updateSignalPlayerIds = async () => {
    if (!currentAgent || !merchant?.id) {
      return;
    }

    const signalPlayerId = localStorage.getItem("fuzeySignalPlayerId");

    if (
      signalPlayerId &&
      (!currentAgent.playerIds ||
        !currentAgent.playerIds.includes(signalPlayerId))
    ) {
      const selectedMerchant = currentAgent.merchants.find(
        (m) => m.id === merchant.id
      )!;

      AgentsService.updateAgent(
        auth0Context,
        {
          id: Number(currentAgent.id),
          name: currentAgent.name,
          surname: currentAgent.surname,
          email: currentAgent.email,
          role_id: selectedMerchant.role.id,
          active: currentAgent.isActive(merchant.id),
          profile_picture_url: currentAgent.profilePic,
          player_ids: [...(currentAgent.playerIds || []), signalPlayerId],
          style_preferences: {
            chat_background:
              currentAgent.stylePreferences?.chatBackground || null,
            color_scheme: currentAgent.stylePreferences?.colorScheme || null,
          },
        },
        merchant.id
      );
    }
  };

  // Tags, attributes, and custom fields are now fetched automatically via TanStack Query hooks
  // when components use useTags(), useAttributes(), and useCustomFieldKeys()

  useEffect(() => {
    if (!merchant?.id) {
      return;
    }

    localStorage.setItem("fuzey:merchant:country", merchant.country);

    if (!notificationPermissionRequested) {
      askForNotificationsPermission();
      setNotificationPermissionRequested(true);
    }
  }, [merchant?.id, notificationPermissionRequested]);

  useEffect(() => {
    const handlePlayerId = () => {
      updateSignalPlayerIds();
    };

    window.addEventListener("fuzey:onesignal:playerid", handlePlayerId);

    updateSignalPlayerIds();

    return () => {
      window.removeEventListener("fuzey:onesignal:playerid", handlePlayerId);
    };
  }, [updateSignalPlayerIds]);

  // Agents are now fetched automatically via useMerchantAgents hook

  const handleTagCreated = (args: SocketTagCreated) => {
    if (!merchant?.groupId || args.tag.group_id !== merchant.groupId) {
      return;
    }

    // Update TanStack Query cache
    queryClient.setQueryData<TagsDomain[]>(
      tagsQueryKey(merchant.groupId),
      (old) => {
        if (!old) return old;
        // Check if tag already exists
        if (old.find((t) => t.id === args.tag.id)) {
          return old;
        }
        // Add new tag with color
        const color = colorList[old.length % colorList.length];
        const newTag = new TagsDomain(args.tag.name, color, args.tag.id);
        return [...old, newTag];
      }
    );

    // Note: Redux addTag dispatch removed - using TanStack Query cache only
  };

  const handleTagDeleted = (args: SocketTagDeleted) => {
    if (!merchant?.groupId || args.tag.group_id !== merchant.groupId) {
      return;
    }

    // Update TanStack Query cache
    removeTagFromCache(queryClient, merchant.groupId, args.tag.id);
  };

  const handleAgentUpdated = (args: SocketAgentUpdated) => {
    if (currentAgent && args.agent.id === currentAgent.id) {
      AgentsService.getCurrentAgent(auth0Context)
        .then((agent) => {
          if (!agent) {
            return;
          }

          // Update TanStack Query cache
          queryClient.setQueryData(currentAgentQueryKey(), agent);
        })
        .catch((error) => {
          if (error.response?.status === 404) {
            endSession(auth0Context.logout);
          }
        });
    }
  };

  const handleChannelCreated = (args: ChannelCreatedMessage) => {
    if (!merchant?.id || args.merchant_id !== merchant.id) {
      return;
    }

    if (args.channel_name === "email" && args.provider === "google") {
      // Update TanStack Query cache
      queryClient.setQueryData(
        merchantQueryKey(merchant.id),
        (old: typeof merchant | null) => {
          if (!old) return old;
          return {
            ...old,
            integrations: {
              ...old.integrations,
              [IntegrationName.GMAIL]: {
                ...old.integrations[IntegrationName.GMAIL],
                connected: true,
              },
            },
          };
        }
      );
    }
  };

  const handleAppIsOutdated = (args: AppIsOutdatedMessage) => {
    const toastId = "app-update-notification";
    const currentBuildNumber = process.env.REACT_APP_BUILD_NUMBER
      ? parseInt(process.env.REACT_APP_BUILD_NUMBER, 10)
      : null;
    const latestBuildNumber = args.build_number;

    if (
      toastTextRef.current !== null ||
      toaster.isVisible(toastId) ||
      !latestBuildNumber ||
      (currentBuildNumber && latestBuildNumber <= currentBuildNumber)
    ) {
      return;
    }

    toaster.create({
      title: (
        <Text ref={toastTextRef} fontWeight="normal">
          A new version of the app is available.{" "}
          <Button
            variant="subtle"
            color="inherit"
            fontWeight="bold"
            px={1}
            onClick={() => {
              location.reload();
            }}
          >
            Refresh <Icon as={LuRefreshCcw} />
          </Button>{" "}
          to enjoy the latest updates.
        </Text>
      ),
      type: "info",
      id: toastId,
      action: {
        label: "Close",
        onClick: () => {
          toaster.dismiss(toastId);
        },
      },
      duration: undefined,
    });
  };

  const handleInAppNotificationCreated = (args: InAppNotificationCreated) => {
    if (
      !merchant?.id ||
      args.notification.merchant_id !== merchant.id ||
      currentAgent?.id !== args.notification.agent_id
    ) {
      return;
    }

    const notification = fromDtoToDomain(args.notification);

    // Update TanStack Query cache
    appendNotificationToCache(queryClient, merchant.id, notification);
  };

  useEffect(() => {
    if (!isInitialLoading && merchant?.id) {
      addEventHandler("agent_updated", handleAgentUpdated);
      addEventHandler("tag_created", handleTagCreated);
      addEventHandler("tag_deleted", handleTagDeleted);
      addEventHandler("app_is_outdated", handleAppIsOutdated);
      addEventHandler("channel_created", handleChannelCreated);
      addEventHandler(
        "in_app_notification_created",
        handleInAppNotificationCreated
      );
      return () => {
        removeEventHandler("agent_updated", handleAgentUpdated);
        removeEventHandler("tag_created", handleTagCreated);
        removeEventHandler("tag_deleted", handleTagDeleted);
        removeEventHandler("app_is_outdated", handleAppIsOutdated);
        removeEventHandler("channel_created", handleChannelCreated);
        removeEventHandler(
          "in_app_notification_created",
          handleInAppNotificationCreated
        );
      };
    }
  }, [
    // `socket` is a dep so these handlers (re-)attach once the socket connects.
    // The guard no longer blocks first render on the socket, so this effect can
    // first run while socket is still null (addEventHandler no-ops), then re-run
    // to attach for real when the connection is established.
    socket,
    addEventHandler,
    removeEventHandler,
    isInitialLoading,
    merchant?.id,
    handleAgentUpdated,
    handleTagCreated,
    handleTagDeleted,
    handleAppIsOutdated,
    handleChannelCreated,
    handleInAppNotificationCreated,
  ]);

  useEffect(() => {
    if (!isInitialLoading && merchant?.id && socket) {
      socket.on("tag_deleted", handleTagDeleted);

      return () => {
        socket.off("tag_deleted", handleTagDeleted);
      };
    }
  }, [socket, merchant?.id, isInitialLoading, handleTagDeleted]);

  useEffect(() => {
    if (
      !currentAgent ||
      !currentAgent.stylePreferences ||
      !currentAgent.stylePreferences.colorScheme
    ) {
      return;
    }

    dispatch(setColorScheme(currentAgent.stylePreferences.colorScheme));
  }, [currentAgent]);

  useEffect(() => {
    if (!currentAgent || !merchant?.id) {
      return;
    }

    if (process.env.REACT_APP_ENVIRONMENT === "production") {
      import("logrocket").then(({ default: LogRocket }) => {
        LogRocket.identify(`${merchant.id}:${currentAgent.id}`, {
          name: currentAgent.getFullName(),
          email: currentAgent.email,
          agentId: currentAgent.id,
          merchantId: merchant.id,
          merchantName: merchant.name,
          groupId: merchant.groupId,
          merchantCountry: merchant.country,
        });
      });
    }
  }, [currentAgent, merchant?.id]);

  // websocket handlers are declared above to avoid TDZ issues

  useEffect(() => {
    dispatch(setColorMode((colorMode as "dark" | "light") || "light"));
  }, [colorMode]);

  // Hard guard: don't render anything until merchant is loaded
  // This ensures merchant is guaranteed to exist after this point.
  // Deliberately NOT gated on `socket`: the socket is opened lazily via
  // requestIdleCallback (up to a 3s delay on a busy main thread), and it's only
  // needed for live updates — not for first render. The socket event handlers
  // below attach once it connects (see the `socket` dep on their effect).
  if (
    isInitialLoading ||
    !user ||
    !currentAgent ||
    !merchant ||
    !merchant.id ||
    (agentsLoading && !agents.length)
  ) {
    return (
      <Flex justifyContent="center" alignItems="center" h="100%">
        <Spinner size="xl" />
      </Flex>
    );
  }

  // After this guard, merchant is guaranteed to be non-null
  // Provide it via context so child components can use useRequiredMerchant()
  return (
    <MerchantContext.Provider value={merchant}>
      <PrivatePageWrapper>{children}</PrivatePageWrapper>
      {!isMobile && !isAdminRoute && canAccessAiChats(currentAgent) && (
        <Suspense fallback={null}>
          <AiChatWidget />
          <AiChatLauncher />
        </Suspense>
      )}
    </MerchantContext.Provider>
  );
};

export default PrivateRoute;
